import { BALANCE } from '../config/balance';
import { clamp } from '../utils/math';

/** How long a head sample lingers in the blurred trail, seconds. */
export const HEAD_TRAIL_SECONDS = 1.3;
/** Most recent head samples kept, so a stalled tab cannot grow the buffer. */
const MAX_TRAIL = 120;

interface TrailSample {
  x: number;
  y: number;
  /** Seconds, from the same clock the renderer is given. */
  t: number;
}

/**
 * The calibration map: a soft, blurred picture of where the head is in the
 * camera frame, with a marker for where that position lands on the off-axis
 * screen.
 *
 * It is deliberately not an average or a density heat map — it is a short,
 * fading trail of the last second or so of positions, drawn as overlapping
 * glow, so the blob reads as *where the head is now* rather than where it has
 * spent the most time. The maths (buffering, normalising, the dead-zone box) is
 * pure and tested; the drawing is a thin layer that no-ops without a 2D context
 * (jsdom), exactly like the map editor.
 */
export class HeadPositionMap {
  private readonly context: CanvasRenderingContext2D | null;
  private readonly trail: TrailSample[] = [];
  /** The mapped screen point, normalised -1..1 on each axis. */
  private screenX = 0;
  private screenY = 0;
  private hasScreen = false;
  /** The last raw sample, for tooling and tests. */
  lastPoint: { x: number; y: number } | null = null;
  /** Dead zone as a fraction of the reach, so the box matches the response. */
  private deadFraction = clamp(
    BALANCE.headTracking.bodyShiftDeadZone / BALANCE.camera.offAxis.maxViewerX,
    0,
    1,
  );

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.context = canvas.getContext('2d');
  }

  /**
   * Set the dead-zone box from the current reach, so a calibrated setup draws
   * the same *fraction* of a lean as an uncalibrated one.
   */
  setReach(deadZone: number, reach: number): void {
    this.deadFraction = reach > 1e-4 ? clamp(deadZone / reach, 0, 1) : 0;
  }

  /** How many samples are live in the trail. */
  get trailLength(): number {
    return this.trail.length;
  }

  /** The mapped screen point, -1..1, or null before the first update. */
  get screenPoint(): { x: number; y: number } | null {
    return this.hasScreen ? { x: this.screenX, y: this.screenY } : null;
  }

  /**
   * Add a measured head position in the camera frame, `0..1` from the
   * top-left. A null position (no face this frame) adds nothing.
   */
  sampleFrame(position: { x: number; y: number } | null, now: number): void {
    if (!position) return;
    this.lastPoint = { x: position.x, y: position.y };
    this.trail.push({ x: clamp(position.x, 0, 1), y: clamp(position.y, 0, 1), t: now });
    if (this.trail.length > MAX_TRAIL) this.trail.shift();
    this.prune(now);
  }

  /** Set the mapped screen point, normalised -1..1 on each axis. */
  setScreen(x: number, y: number): void {
    this.screenX = clamp(x, -1, 1);
    this.screenY = clamp(y, -1, 1);
    this.hasScreen = true;
  }

  /** Forget the trail and the marker. */
  clear(): void {
    this.trail.length = 0;
    this.hasScreen = false;
    this.lastPoint = null;
  }

  private prune(now: number): void {
    const oldest = now - HEAD_TRAIL_SECONDS;
    while (this.trail.length > 0 && (this.trail[0] as TrailSample).t < oldest) this.trail.shift();
  }

  /** Draw the trail, the frame framing and the screen marker. */
  render(now: number): void {
    const context = this.context;
    if (!context) return;
    this.prune(now);

    const width = this.canvas.width;
    const height = this.canvas.height;
    context.clearRect(0, 0, width, height);

    // The screen mapping underneath the trail: the clamp the window runs in and
    // the body-sway dead zone, so a lean can be read against both.
    this.drawScreenGuides(context, width, height);

    // The blurred head position: overlapping radial glows, brightest at the
    // newest sample, additive so the trail reads as one soft shape.
    context.save();
    context.globalCompositeOperation = 'lighter';
    for (const sample of this.trail) {
      const age = clamp((now - sample.t) / HEAD_TRAIL_SECONDS, 0, 1);
      const strength = (1 - age) * (1 - age);
      this.drawGlow(context, sample.x * width, sample.y * height, strength, width);
    }
    context.restore();

    // The head centre, crisply, so the current position is never a guess.
    const current = this.trail[this.trail.length - 1];
    if (current) {
      context.fillStyle = 'rgba(215, 255, 245, 0.95)';
      context.beginPath();
      context.arc(current.x * width, current.y * height, 3, 0, Math.PI * 2);
      context.fill();
    }
  }

  private drawGlow(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    strength: number,
    width: number,
  ): void {
    if (strength <= 0.01) return;
    const radius = width * 0.16;
    const gradient = context.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, `rgba(99, 240, 208, ${0.45 * strength})`);
    gradient.addColorStop(0.6, `rgba(80, 170, 190, ${0.18 * strength})`);
    gradient.addColorStop(1, 'rgba(80, 170, 190, 0)');
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fill();
  }

  private drawScreenGuides(context: CanvasRenderingContext2D, width: number, height: number): void {
    const deadFraction = this.deadFraction;
    const halfX = width / 2;
    const halfY = height / 2;
    const centreX = halfX;
    const centreY = halfY;

    // The camera frame: the canvas edge *is* the edge of the frame, so the
    // blurred head position reads relative to it. Corner ticks and a centre
    // cross make the frame obvious without drawing the camera image.
    this.drawCameraFrame(context, width, height);

    // The dead zone: the middle where a lean does not move the body.
    const innerX = deadFraction * halfX;
    const innerY = deadFraction * halfY;
    context.strokeStyle = 'rgba(255, 179, 71, 0.4)';
    context.setLineDash([3, 3]);
    context.strokeRect(centreX - innerX, centreY - innerY, innerX * 2, innerY * 2);
    context.setLineDash([]);

    // The mapped screen point, with a lead line from the centre. A full lean
    // reaches the frame edge, so the two share one scale.
    if (this.hasScreen) {
      const mx = centreX + this.screenX * halfX;
      const my = centreY + this.screenY * halfY;
      context.strokeStyle = 'rgba(255, 226, 168, 0.5)';
      context.beginPath();
      context.moveTo(centreX, centreY);
      context.lineTo(mx, my);
      context.stroke();
      context.fillStyle = '#ffe2a8';
      context.beginPath();
      context.arc(mx, my, 4, 0, Math.PI * 2);
      context.fill();
    }
  }

  /** The edges of the camera frame: a bright border, corner ticks and a cross. */
  private drawCameraFrame(context: CanvasRenderingContext2D, width: number, height: number): void {
    context.strokeStyle = 'rgba(159, 246, 255, 0.45)';
    context.lineWidth = 1;
    context.strokeRect(0.5, 0.5, width - 1, height - 1);

    // Corner ticks, so an off-centre head is read against a clear frame.
    const tick = Math.min(width, height) * 0.08;
    context.strokeStyle = 'rgba(159, 246, 255, 0.75)';
    context.lineWidth = 2;
    const corners: Array<[number, number, number, number]> = [
      [0, 0, 1, 1],
      [width, 0, -1, 1],
      [0, height, 1, -1],
      [width, height, -1, -1],
    ];
    for (const [x, y, dx, dy] of corners) {
      context.beginPath();
      context.moveTo(x, y + dy * tick);
      context.lineTo(x, y);
      context.lineTo(x + dx * tick, y);
      context.stroke();
    }

    // A faint centre cross for the neutral position.
    context.strokeStyle = 'rgba(223, 232, 242, 0.18)';
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(width / 2, 0);
    context.lineTo(width / 2, height);
    context.moveTo(0, height / 2);
    context.lineTo(width, height / 2);
    context.stroke();
  }
}
